import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { getCompanyList } from '../../../api';
import { AutoTable } from '../../../components/auto-table';
import {
  CommonButton,
  CommonButtonGroup,
} from '../../../components/button/main';
import { ModalWrap } from '../../../components/modal-wrap';
import { CommonTabs } from '../../../components/tabs';
import { getLocalTime } from '../../../lib';
import { wrapRequestArea } from '../../../lib/http/lib';
import getFormatMessage from '../../../locales/get-format-message';
import { ICommonSupportPageProps } from '../../../model';
import { CommonStatusStep } from '../../../model/api-modules';
import {
  CompanyRegion,
  IAPICompanyFullInfo,
  MDMAuthStatus,
  StudioCertifiedType,
} from '../../../model/api-modules/company';
import { SystemMenuId } from '../../../model/system/menu';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../../store/modules/common';
import styles from './index.module.scss';
import { RejectModal } from './reject';
import { ResolveModal } from './resolve';
import { SupportCompanyInfoViewPanel } from './view-panel';
import { IRootState } from '../../../store/reducers';

export const CompanyAuth = (props: ICommonSupportPageProps) => {
  const { site } = props;
  const [activeTab, setActiveTab] = useState<string>(
    String(CommonStatusStep.Pending)
  );
  const [showResolveModal, setShowResolveModal] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [inAuthInfo, setInAuthInfo] = useState<IAPICompanyFullInfo>();
  const [inViewInfo, setInViewInfo] = useState<IAPICompanyFullInfo>();
  const [list, setList] = useState<ReadonlyArray<IAPICompanyFullInfo>>();
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  const menuDispatch = useDispatch<Dispatch<CommonAction>>();
  menuDispatch({
    type: ReduxCommonActionType.SELECT_MENU_ID,
    result: SystemMenuId.CompanyAuth,
  });

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const resList = await wrapRequestArea(getCompanyList, site);
    setList(resList);
  };

  const showCompanyInfo = (companyInfo: IAPICompanyFullInfo) => {
    setInViewInfo(companyInfo);
  };

  const resolveCompany = (companyInfo: IAPICompanyFullInfo) => {
    setInAuthInfo(companyInfo);
    setShowResolveModal(true);
  };

  const rejectCompany = (companyInfo: IAPICompanyFullInfo) => {
    setInAuthInfo(companyInfo);
    setShowRejectModal(true);
  };

  const onConfirmAuth = () => {
    init();
    onCancelAuth();
  };

  const onCancelAuth = () => {
    setInAuthInfo(undefined);
    setShowResolveModal(false);
    setShowRejectModal(false);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };
  const tabs = [
    {
      key: CommonStatusStep.Pending.toString(),
      label: 'Pending',
      content: null,
    },
    {
      key: CommonStatusStep.Resolve.toString(),
      label: 'Resolved',
      content: null,
    },
    {
      key: CommonStatusStep.Reject.toString(),
      label: 'Rejected',
      content: null,
    },
  ];

  const filterList = list?.filter((item) => String(item.Status) === activeTab);
  const onChangedFilterValues = () => {};
  return (
    <div>
      <div>
        <CommonTabs
          activeKey={activeTab.toString()}
          onChange={handleTabChange}
          items={tabs}
        ></CommonTabs>
        {/* 临时处理存量工作室创建环境屏蔽 */}
        {/* <AssistAddEnv /> */}
      </div>
      <div className={styles.companyAuth}>
        <AutoTable
          list={filterList}
          configFields={[
            {
              key: 'id',
              name: getFormatMessage('company_id'),
            },

            {
              key: 'StudioID',
              name: getFormatMessage('studio_id'),
            },
            {
              key: 'certifiedType',
              name: getFormatMessage('认证类型'),
              tableExtension: {
                renderRow(key, value, record) {
                  if (
                    record.certifiedType === StudioCertifiedType.InternalKpiCode
                  ) {
                    return '内部工作室认证';
                  }

                  return '企业认证';
                },
              },
            },
            {
              key: 'Name',
              name: getFormatMessage('company_name'),
              tableExtension: {
                renderRow(key, value, record) {
                  return record.Name || record.InternalCertInfo?.projectName;
                },
              },
            },
            {
              key: 'Region',
              name: getFormatMessage('company_area'),
              tableExtension: {
                renderRow(key, value, record) {
                  if (
                    record.certifiedType === StudioCertifiedType.InternalKpiCode
                  ) {
                    return '--';
                  }
                  if (value === CompanyRegion.ChinaLocal) {
                    return '中国大陆';
                  }
                  return <></>;
                },
              },
            },
            {
              key: 'CreatorBy',
              name: getFormatMessage('company_auth_applier'),
            },
            {
              key: 'ModifyTime',
              name: getFormatMessage('申请时间'),
              tableExtension: {
                renderRow(key, value, record) {
                  if (record.Status === CommonStatusStep.Pending) {
                    return getLocalTime(
                      value,
                      userInfo?.UserSettings?.Timezone
                    );
                  }
                  return getLocalTime(
                    record.CreateTime,
                    userInfo?.UserSettings?.Timezone
                  );
                },
              },
            },
            {
              key: 'Status',
              name: getFormatMessage('审核状态'),
              tableExtension: {
                renderRow(key, value, record) {
                  const info = record as IAPICompanyFullInfo;
                  const isMDMPending =
                    typeof info.MDMStatus === 'number' &&
                    [
                      MDMAuthStatus.UnderReview,
                      MDMAuthStatus.ApprovalVerify,
                    ].includes(info.MDMStatus);

                  const statusMap = {
                    [CommonStatusStep.Pending]: '审核中',
                    [CommonStatusStep.Resolve]: '已通过',
                    [CommonStatusStep.Reject]: '已拒绝',
                  };

                  if (
                    info.ManualStatus === CommonStatusStep.Resolve &&
                    isMDMPending
                  ) {
                    return 'MDM审核中';
                  }

                  return statusMap[value as CommonStatusStep] || '';
                },
              },
            },
            {
              key: 'operate',
              name: '操作',
              tableExtension: {
                renderRow(key, value, record) {
                  const btns = [
                    <CommonButton
                      type="link"
                      onClick={showCompanyInfo.bind(null, record)}
                    >
                      查看
                    </CommonButton>,
                  ];
                  if (
                    record.Status === CommonStatusStep.Pending &&
                    record.ManualStatus !== CommonStatusStep.Resolve
                  ) {
                    btns.push(
                      <CommonButton
                        type="link"
                        onClick={resolveCompany.bind(null, record)}
                      >
                        通过
                      </CommonButton>,
                      <CommonButton
                        type="link"
                        onClick={rejectCompany.bind(null, record)}
                      >
                        拒绝
                      </CommonButton>
                    );
                  }

                  return <CommonButtonGroup>{btns}</CommonButtonGroup>;
                },
              },
            },
          ]}
          layoutExtension={{
            showTable: true,
            showTopbar: false,
            showFilter: true,
          }}
          onChangedFilterValues={onChangedFilterValues}
        />
        {!!inViewInfo && (
          <ModalWrap onClose={() => setInViewInfo(undefined)} width="1000px">
            <SupportCompanyInfoViewPanel
              data={inViewInfo}
              showRejectReasons={true}
              site={site}
            />
          </ModalWrap>
        )}

        {showResolveModal && inAuthInfo && (
          <ResolveModal
            companyInfo={inAuthInfo}
            onOk={onConfirmAuth}
            onCancel={onCancelAuth}
          />
        )}
        {showRejectModal && inAuthInfo && (
          <RejectModal
            companyInfo={inAuthInfo}
            onOk={onConfirmAuth}
            onCancel={onCancelAuth}
          />
        )}
      </div>
    </div>
  );
};
